import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  Headers,
  LoadingSpinner,
  SearchBarWithDropdown,
  createDebounce,
  formatDate,
} from '@tw/tw-component-library';
import { Button } from '@/ui';
import { UseAuth } from '@/contexts/AuthContext';
import {
  UserWorkspace,
  getUserWorkspace,
} from '@/apis/core/workspace/get/getUserWorkspace';
import {
  UserAuditLogEntry,
  UserAuditLogResponse,
  postUserAuditLog,
} from '@/apis/core/UserAuditLog/post/postAuditLog';
import { postUserAuditLogAll } from '@/apis/core/UserAuditLog/post/postAuditLogAll';
import { UserResponse, getUser } from '@/apis/core/user/get/getUser';
import {
  UserSearchResult,
  searchUsers,
} from '@/apis/core/user/get/searchUsers';
import {
  getUserDisplayNameFromRecord,
  getUserDisplayNameWithFetch,
} from '@/utils/userDisplayName';
import {
  AppliedFilterChip,
  AppliedFiltersRow,
  BarFill,
  BarLabel,
  BarRow,
  BarTrack,
  BarValue,
  ContentContainer,
  ControlsRow,
  DashboardSection,
  DateInput,
  EmptyState,
  FiltersPanel,
  HorizontalBars,
  PageContainer,
  Select,
  SectionSubtitle,
  SectionTitle,
  StatCard,
  StatLabel,
  StatValue,
  StatsGrid,
  Table,
  TableWrapper,
  UserActionList,
  UserActionRow,
  UserCard,
  UserCardSubtle,
  UserCardsGrid,
  UserCardTitle,
} from './Anylitics.styles';

type WorkspaceTypeKey = 'Private' | 'Collaborative' | 'Standard';
type AuditMode = 'selectedUser' | 'allData';
type UserGroupFilter = 'all' | 'superUsers';

const WORKSPACE_TYPES: WorkspaceTypeKey[] = [
  'Private',
  'Collaborative',
  'Standard',
];

const SUPER_USER_EMAILS = new Set(
  [
    'S.Bamber@taylorwessing.com',
    'C.Royle@taylorwessing.com',
    'D.Smith@taylorwessing.com',
    'M.Egan@taylorwessing.com',
    'T.Wood@taylorwessing.com',
    'A.Domuta@taylorwessing.com',
    'S.Cox@taylorwessing.com',
    'S.Neeliyara@taylorwessing.com',
    'J.Yandell@taylorwessing.com',
    'L.Pritchard@taylorwessing.com',
    'S.Maher@taylorwessing.com',
    'L.Dance@taylorwessing.com',
    'A.Tang@taylorwessing.com',
    'F.Harland@taylorwessing.com',
    'K.Woods@taylorwessing.com',
    'S.Spillane@taylorwessing.com',
    'N.Marcora@taylorwessing.com',
    'H.McCormick-Hill@taylorwessing.com',
    'H.Lowe@taylorwessing.com',
    'N.Edward@taylorwessing.com',
    'V.Kwitshana@taylorwessing.com',
    'M.Bernstein@taylorwessing.com',
    'K.Arnold@taylorwessing.com',
    'L.Black@taylorwessing.com',
    'D.Phillips@taylorwessing.com',
    'F.Naz@taylorwessing.com',
    'J.Drakes@taylorwessing.com',
    'D.Haynes@taylorwessing.com',
    'Ni.Davies@taylorwessing.com',
    'S.Bell@taylorwessing.com',
    'K.Kocaj@taylorwessing.com',
    'P.McMonagle@taylorwessing.com',
    'T.Howard@taylorwessing.com',
    'S.Marchant@taylorwessing.com',
    'S.McCone@taylorwessing.com',
    'S.Cooppen@taylorwessing.com',
    'J.Hunt@taylorwessing.com',
    'M.Rendall@taylorwessing.com',
    'L.Mulholland@taylorwessing.com',
    'A.Gill@taylorwessing.com',
    'D.Miller@taylorwessing.com',
    'A.Pitsillides@taylorwessing.com',
  ].map((email) => email.toLowerCase()),
);

const formatDateForInput = (date: Date): string => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

export const Anylitics = () => {
  const { getAccessToken } = UseAuth();
  const [workspaces, setWorkspaces] = useState<UserWorkspace[]>([]);
  const [auditMode, setAuditMode] = useState<AuditMode>('selectedUser');
  const [currentUserId, setCurrentUserId] = useState<string>('');
  const [currentUserRecord, setCurrentUserRecord] = useState<UserResponse | null>(null);
  const [selectedUserId, setSelectedUserId] = useState<string>('');
  const [selectedUserName, setSelectedUserName] = useState<string>('Unknown');
  const [selectedUserSearchInput, setSelectedUserSearchInput] = useState('');
  const [isSelectedUserSearchOpen, setIsSelectedUserSearchOpen] = useState(false);
  const [isSelectedUserSearchLoading, setIsSelectedUserSearchLoading] =
    useState(false);
  const [selectedUserSearchResults, setSelectedUserSearchResults] = useState<
    UserSearchResult[]
  >([]);
  const lastSelectedUserSearchRef = useRef('');
  const [startDateInput, setStartDateInput] = useState(() => {
    const d = new Date();
    d.setDate(d.getDate() - 30);
    return formatDateForInput(d);
  });
  const [endDateInput, setEndDateInput] = useState(() => formatDateForInput(new Date()));
  const [auditLogs, setAuditLogs] = useState<UserAuditLogEntry[]>([]);
  const [auditUserNameMap, setAuditUserNameMap] = useState<Record<string, string>>({});
  const [auditUserDetailsMap, setAuditUserDetailsMap] = useState<
    Record<string, UserResponse>
  >({});
  const [officeFilter, setOfficeFilter] = useState('all');
  const [departmentFilter, setDepartmentFilter] = useState('all');
  const [countryFilter, setCountryFilter] = useState('all');
  const [userGroupFilter, setUserGroupFilter] = useState<UserGroupFilter>('all');
  const [isLoading, setIsLoading] = useState(true);
  const [isAuditLoading, setIsAuditLoading] = useState(false);
  const [isHydratingUserDetails, setIsHydratingUserDetails] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [auditError, setAuditError] = useState<string | null>(null);

  const fetchAnalyticsData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError(null);
      const [data, userResponse] = await Promise.all([
        getUserWorkspace({ getAccessToken }),
        getUser({ getAccessToken }),
      ]);
      const user = Array.isArray(userResponse) ? userResponse[0] : userResponse;
      setWorkspaces(data);
      setCurrentUserRecord(user ?? null);
      const resolvedUserId = String(user?.id ?? '');
      const resolvedUserName = await getUserDisplayNameWithFetch({
        userId: resolvedUserId,
        userRecord: user,
        getAccessToken,
      });
      setCurrentUserId(resolvedUserId);
      setSelectedUserId(resolvedUserId);
      setSelectedUserName(resolvedUserName);
    } catch (err) {
      const message =
        err instanceof Error
          ? err.message
          : 'Failed to load analytics dashboard data';
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }, [getAccessToken]);

  useEffect(() => {
    void fetchAnalyticsData();
  }, [fetchAnalyticsData]);

  const availableUserIds = useMemo(() => {
    const userIdSet = new Set<string>();
    workspaces.forEach((ws) => {
      Object.keys(ws.workspaceUsers ?? {}).forEach((userId) => userIdSet.add(userId));
    });
    if (currentUserId) {
      userIdSet.add(currentUserId);
    }
    return Array.from(userIdSet).sort((a, b) => a.localeCompare(b));
  }, [workspaces, currentUserId]);

  const availableUserOptions = useMemo(
    () =>
      availableUserIds.map((userId) => {
        if (userId === currentUserId) {
          return {
            id: userId,
            label: `${selectedUserName} (${userId})`,
          };
        }
        return { id: userId, label: userId };
      }),
    [availableUserIds, currentUserId, selectedUserName],
  );

  const formatSelectedUserOption = useCallback((option: UserSearchResult) => {
    const displayName = getUserDisplayNameFromRecord(option, option.id);
    const username = option.username ? ` (${option.username})` : '';
    return `${displayName}${username} | ${option.id}`;
  }, []);

  const selectedUserSearchOptions = useMemo(() => {
    return selectedUserSearchResults.map((option) =>
      formatSelectedUserOption(option),
    );
  }, [formatSelectedUserOption, selectedUserSearchResults]);

  const parseSearchUsersResponse = useCallback((response: unknown) => {
    if (Array.isArray(response)) return response as UserSearchResult[];
    if (response && typeof response === 'object') {
      const wrapped = response as { data?: { items?: unknown } };
      if (Array.isArray(wrapped.data?.items)) {
        return wrapped.data.items as UserSearchResult[];
      }
    }
    return [] as UserSearchResult[];
  }, []);

  const handleSelectedUserSearch = useCallback(
    (value: string) => {
      setSelectedUserSearchInput(value);
      const trimmed = value.trim();

      if (!trimmed || trimmed.length < 3) {
        lastSelectedUserSearchRef.current = '';
        setSelectedUserSearchResults([]);
        setIsSelectedUserSearchOpen(false);
        setIsSelectedUserSearchLoading(false);
        return;
      }

      const runSearch = createDebounce(async (searchValue: string) => {
        if (lastSelectedUserSearchRef.current === searchValue) return;
        lastSelectedUserSearchRef.current = searchValue;
        try {
          setIsSelectedUserSearchLoading(true);
          const response = await searchUsers({
            getAccessToken,
            searchValue,
          });
          const parsed = parseSearchUsersResponse(response);
          setSelectedUserSearchResults(parsed);
          setIsSelectedUserSearchOpen(parsed.length > 0);
        } catch {
          setSelectedUserSearchResults([]);
          setIsSelectedUserSearchOpen(false);
        } finally {
          setIsSelectedUserSearchLoading(false);
        }
      }, 300);
      runSearch(trimmed);
    },
    [getAccessToken, parseSearchUsersResponse],
  );

  const handleSelectedUserSearchSelect = useCallback(
    (selectedLabel: string | null) => {
      if (!selectedLabel) return;

      const selectedFromSearch = selectedUserSearchResults.find(
        (option) => formatSelectedUserOption(option) === selectedLabel,
      );

      if (selectedFromSearch) {
        setSelectedUserId(selectedFromSearch.id);
        setSelectedUserName(
          getUserDisplayNameFromRecord(
            selectedFromSearch,
            selectedFromSearch.id,
          ),
        );
        setSelectedUserSearchInput('');
        setSelectedUserSearchResults([]);
        setIsSelectedUserSearchOpen(false);
        return;
      }

      // Keep behavior aligned with invite modal: selection must come from suggestions.
    },
    [
      formatSelectedUserOption,
      selectedUserSearchResults,
    ],
  );

  const parseAuditResponse = (response: unknown): UserAuditLogResponse[] => {
    if (Array.isArray(response)) {
      return response as UserAuditLogResponse[];
    }
    if (response && typeof response === 'object') {
      return [response as UserAuditLogResponse];
    }
    return [];
  };

  const fetchAuditData = useCallback(async () => {
    if (auditMode === 'selectedUser' && !selectedUserId) return;
    try {
      setIsAuditLoading(true);
      setAuditError(null);

      const startDate = new Date(`${startDateInput}T00:00:00`);
      const endDate = new Date(`${endDateInput}T23:59:59.999`);
      if (Number.isNaN(startDate.getTime()) || Number.isNaN(endDate.getTime())) {
        throw new Error('Please provide a valid date range.');
      }
      if (startDate > endDate) {
        throw new Error('Start date cannot be after end date.');
      }

      const body = {
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
        results: '10000',
        offset: '0',
        users: auditMode === 'selectedUser' ? [selectedUserId] : [],
      };

      const response =
        auditMode === 'allData'
          ? await postUserAuditLogAll({ getAccessToken, body })
          : await postUserAuditLog({ getAccessToken, body });

      const responses = parseAuditResponse(response);
      const logs = responses.flatMap((res) => res.logs ?? []);
      setAuditLogs(logs);
    } catch (err) {
      const message =
        err instanceof Error ? err.message : 'Failed to load user audit data';
      setAuditError(message);
      setAuditLogs([]);
    } finally {
      setIsAuditLoading(false);
    }
  }, [auditMode, endDateInput, getAccessToken, selectedUserId, startDateInput]);

  useEffect(() => {
    void fetchAuditData();
  }, [fetchAuditData]);

  useEffect(() => {
    const enrichAuditUserNames = async () => {
      const uniqueIds = Array.from(
        new Set(auditLogs.map((log) => log.userId).filter((id): id is string => Boolean(id))),
      );
      if (uniqueIds.length === 0) {
        setAuditUserNameMap({});
        setAuditUserDetailsMap({});
        setIsHydratingUserDetails(false);
        return;
      }

      try {
        setIsHydratingUserDetails(true);
        const response = await getUser({
          getAccessToken,
          userIds: uniqueIds,
        });
        const users = Array.isArray(response) ? response : [response];
        const usersById = new Map(users.map((user) => [user.id, user]));

        const currentUserLabel = getUserDisplayNameFromRecord(
          currentUserRecord,
          selectedUserName,
        );

        const map = Object.fromEntries(
          uniqueIds.map((userId) => {
            const user = usersById.get(userId);
            if (user) {
              return [userId, getUserDisplayNameFromRecord(user, userId)] as const;
            }
            if (userId === currentUserId) {
              return [userId, currentUserLabel] as const;
            }
            return [userId, userId] as const;
          }),
        );
        setAuditUserNameMap(map);
        setAuditUserDetailsMap(Object.fromEntries(users.map((u) => [u.id, u])));
      } catch {
        const map = Object.fromEntries(uniqueIds.map((id) => [id, id]));
        setAuditUserNameMap(map);
        setAuditUserDetailsMap({});
      } finally {
        setIsHydratingUserDetails(false);
      }
    };

    void enrichAuditUserNames();
  }, [auditLogs, currentUserId, currentUserRecord, getAccessToken, selectedUserName]);

  const totals = useMemo(() => {
    const totalWorkspaces = workspaces.length;
    const totalMembers = workspaces.reduce(
      (sum, ws) => sum + Object.keys(ws.workspaceUsers ?? {}).length,
      0,
    );
    const totalDocuments = workspaces.reduce(
      (sum, ws) => sum + (ws.dataRoom?.workspaceDataRoomFiles?.length ?? 0),
      0,
    );
    const totalCollections = workspaces.reduce(
      (sum, ws) => sum + (ws.dataRoom?.workspaceDataRoomCollections?.length ?? 0),
      0,
    );
    const totalAskSessions = workspaces.reduce(
      (sum, ws) => sum + (ws.askSessions?.length ?? 0),
      0,
    );

    return {
      totalWorkspaces,
      totalMembers,
      totalDocuments,
      totalCollections,
      totalAskSessions,
    };
  }, [workspaces]);

  const workspaceTypeCounts = useMemo(() => {
    const counts: Record<WorkspaceTypeKey, number> = {
      Private: 0,
      Collaborative: 0,
      Standard: 0,
    };

    workspaces.forEach((ws) => {
      if (WORKSPACE_TYPES.includes(ws.workspaceType as WorkspaceTypeKey)) {
        counts[ws.workspaceType as WorkspaceTypeKey] += 1;
      }
    });

    return counts;
  }, [workspaces]);

  const workspaceRows = useMemo(() => {
    return [...workspaces].sort(
      (a, b) =>
        new Date(b.lastUpdate).getTime() - new Date(a.lastUpdate).getTime(),
    );
  }, [workspaces]);

  const maxWorkspaceTypeValue = Math.max(...Object.values(workspaceTypeCounts), 1);

  const auditByAction = useMemo(() => {
    const filteredLogs = auditLogs.filter((log) => {
      if (userGroupFilter !== 'superUsers') return true;
      const userId = log.userId ?? '';
      const user = auditUserDetailsMap[userId];
      const email = (user?.username ?? '').toLowerCase();
      return SUPER_USER_EMAILS.has(email);
    });

    const map: Record<string, number> = {};
    filteredLogs.forEach((log) => {
      map[log.action] = (map[log.action] ?? 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [auditLogs, auditUserDetailsMap, userGroupFilter]);

  const auditByEntityType = useMemo(() => {
    const filteredLogs = auditLogs.filter((log) => {
      if (userGroupFilter !== 'superUsers') return true;
      const userId = log.userId ?? '';
      const user = auditUserDetailsMap[userId];
      const email = (user?.username ?? '').toLowerCase();
      return SUPER_USER_EMAILS.has(email);
    });

    const map: Record<string, number> = {};
    filteredLogs.forEach((log) => {
      map[log.entityType] = (map[log.entityType] ?? 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [auditLogs, auditUserDetailsMap, userGroupFilter]);

  const auditByUser = useMemo(() => {
    const filteredLogs = auditLogs.filter((log) => {
      if (userGroupFilter !== 'superUsers') return true;
      const userId = log.userId ?? '';
      const user = auditUserDetailsMap[userId];
      const email = (user?.username ?? '').toLowerCase();
      return SUPER_USER_EMAILS.has(email);
    });

    const map: Record<string, number> = {};
    filteredLogs.forEach((log) => {
      const userId = log.userId ?? 'Unknown';
      const label = auditUserNameMap[userId] ?? userId;
      map[label] = (map[label] ?? 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8);
  }, [auditLogs, auditUserDetailsMap, auditUserNameMap, userGroupFilter]);

  const perUserStats = useMemo(() => {
    const grouped = new Map<
      string,
      {
        userId: string;
        displayName: string;
        total: number;
        actions: Record<string, number>;
      }
    >();

    const filteredLogs = auditLogs.filter((log) => {
      if (userGroupFilter !== 'superUsers') return true;
      const userId = log.userId ?? '';
      const user = auditUserDetailsMap[userId];
      const email = (user?.username ?? '').toLowerCase();
      return SUPER_USER_EMAILS.has(email);
    });

    filteredLogs.forEach((log) => {
      const userId = log.userId ?? 'Unknown';
      const displayName = auditUserNameMap[userId] ?? userId;
      const existing = grouped.get(userId) ?? {
        userId,
        displayName,
        total: 0,
        actions: {},
      };
      existing.total += 1;
      existing.actions[log.action] = (existing.actions[log.action] ?? 0) + 1;
      grouped.set(userId, existing);
    });

    return Array.from(grouped.values())
      .map((entry) => ({
        ...entry,
        userDetails: auditUserDetailsMap[entry.userId],
        topActions: Object.entries(entry.actions)
          .sort((a, b) => b[1] - a[1])
          .slice(0, 6),
      }))
      .sort((a, b) => b.total - a.total);
  }, [auditLogs, auditUserDetailsMap, auditUserNameMap, userGroupFilter]);

  const perUserFilterOptions = useMemo(() => {
    const offices = new Set<string>();
    const departments = new Set<string>();
    const countries = new Set<string>();

    perUserStats.forEach((user) => {
      if (user.userDetails?.office) offices.add(user.userDetails.office);
      if (user.userDetails?.department) departments.add(user.userDetails.department);
      if (user.userDetails?.country) countries.add(user.userDetails.country);
    });

    return {
      offices: Array.from(offices).sort((a, b) => a.localeCompare(b)),
      departments: Array.from(departments).sort((a, b) => a.localeCompare(b)),
      countries: Array.from(countries).sort((a, b) => a.localeCompare(b)),
    };
  }, [perUserStats]);

  const filteredPerUserStats = useMemo(() => {
    return perUserStats.filter((user) => {
      const office = user.userDetails?.office ?? '';
      const department = user.userDetails?.department ?? '';
      const country = user.userDetails?.country ?? '';

      if (officeFilter !== 'all' && office !== officeFilter) return false;
      if (departmentFilter !== 'all' && department !== departmentFilter) return false;
      if (countryFilter !== 'all' && country !== countryFilter) return false;
      return true;
    });
  }, [countryFilter, departmentFilter, officeFilter, perUserStats]);

  const appliedFilters = useMemo(() => {
    const chips = [
      `Mode: ${auditMode === 'allData' ? 'All Data' : 'Selected User'}`,
      `Range: ${startDateInput} to ${endDateInput}`,
    ];
    if (auditMode === 'selectedUser') {
      chips.push(`User: ${selectedUserName}`);
    }
    if (auditMode === 'allData') {
      if (userGroupFilter === 'superUsers') chips.push('User Group: SuperUsers');
      if (officeFilter !== 'all') chips.push(`Office: ${officeFilter}`);
      if (departmentFilter !== 'all') chips.push(`Department: ${departmentFilter}`);
      if (countryFilter !== 'all') chips.push(`Country: ${countryFilter}`);
    }
    return chips;
  }, [
    auditMode,
    countryFilter,
    departmentFilter,
    endDateInput,
    officeFilter,
    userGroupFilter,
    selectedUserName,
    startDateInput,
  ]);

  if (isLoading) {
    return <LoadingSpinner variant="fullPage" />;
  }

  if (error) {
    return (
      <PageContainer>
        <ContentContainer>
          <Headers headerType="h2">Anylitics</Headers>
          <SectionSubtitle>{error}</SectionSubtitle>
        </ContentContainer>
      </PageContainer>
    );
  }

  return (
    <PageContainer>
      <ContentContainer>
        <div>
          <Headers headerType="h1">Anylitics</Headers>
          <SectionSubtitle>
            API-powered dashboard built from existing workspace endpoints.
          </SectionSubtitle>
        </div>

        <DashboardSection>
          <SectionTitle>Overview</SectionTitle>
          <StatsGrid>
            <StatCard>
              <StatLabel>Workspaces</StatLabel>
              <StatValue>{totals.totalWorkspaces}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Members (total)</StatLabel>
              <StatValue>{totals.totalMembers}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Ask Sessions</StatLabel>
              <StatValue>{totals.totalAskSessions}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Documents</StatLabel>
              <StatValue>{totals.totalDocuments}</StatValue>
            </StatCard>
            <StatCard>
              <StatLabel>Collections</StatLabel>
              <StatValue>{totals.totalCollections}</StatValue>
            </StatCard>
          </StatsGrid>
        </DashboardSection>

        <DashboardSection>
          <SectionTitle>Workspace Type Split</SectionTitle>
          <SectionSubtitle>
            Distribution of your current workspaces by type.
          </SectionSubtitle>
          <HorizontalBars>
            {WORKSPACE_TYPES.map((type) => {
              const value = workspaceTypeCounts[type];
              const width = (value / maxWorkspaceTypeValue) * 100;
              return (
                <BarRow key={type}>
                  <BarLabel>{type}</BarLabel>
                  <BarTrack>
                    <BarFill $width={width} />
                  </BarTrack>
                  <BarValue>{value}</BarValue>
                </BarRow>
              );
            })}
          </HorizontalBars>
        </DashboardSection>

        <DashboardSection>
          <SectionTitle>Workspace Details</SectionTitle>
          <SectionSubtitle>
            Last updated workspaces with current API counts.
          </SectionSubtitle>
          <TableWrapper>
            <Table>
              <thead>
                <tr>
                  <th>Workspace</th>
                  <th>Type</th>
                  <th>Members</th>
                  <th>Ask Sessions</th>
                  <th>Documents</th>
                  <th>Collections</th>
                  <th>Last Update</th>
                </tr>
              </thead>
              <tbody>
                {workspaceRows.map((ws) => (
                  <tr key={ws.id}>
                    <td>{ws.title}</td>
                    <td>{ws.workspaceType}</td>
                    <td>{Object.keys(ws.workspaceUsers ?? {}).length}</td>
                    <td>{ws.askSessions?.length ?? 0}</td>
                    <td>{ws.dataRoom?.workspaceDataRoomFiles?.length ?? 0}</td>
                    <td>{ws.dataRoom?.workspaceDataRoomCollections?.length ?? 0}</td>
                    <td>{formatDate(ws.lastUpdate)}</td>
                  </tr>
                ))}
              </tbody>
            </Table>
          </TableWrapper>
        </DashboardSection>

        <DashboardSection>
          <SectionTitle>Audit Activity</SectionTitle>
          <SectionSubtitle>
            Data source: <code>/api/UserAuditLog</code> and <code>/api/UserAuditLog/all</code>.
          </SectionSubtitle>
          <FiltersPanel>
            <ControlsRow>
              <Select
                value={auditMode}
                onChange={(event) => setAuditMode(event.target.value as AuditMode)}
              >
                <option value="selectedUser">Selected user data</option>
                <option value="allData">All data</option>
              </Select>
              <DateInput
                type="date"
                value={startDateInput}
                onChange={(event) => setStartDateInput(event.target.value)}
              />
              <DateInput
                type="date"
                value={endDateInput}
                onChange={(event) => setEndDateInput(event.target.value)}
              />
              <Button variant="outline" onClick={() => void fetchAuditData()}>
                Apply range
              </Button>
              {auditMode === 'allData' && (
                <Button
                  variant="outline"
                  onClick={() => {
                    setUserGroupFilter('all');
                    setOfficeFilter('all');
                    setDepartmentFilter('all');
                    setCountryFilter('all');
                  }}
                >
                  Clear filters
                </Button>
              )}
            </ControlsRow>
            <AppliedFiltersRow>
              {appliedFilters.map((chip) => (
                <AppliedFilterChip key={chip}>{chip}</AppliedFilterChip>
              ))}
            </AppliedFiltersRow>
          </FiltersPanel>

          {auditMode === 'selectedUser' && (
            <>
              <ControlsRow>
                <SearchBarWithDropdown
                  label="Search user"
                  options={selectedUserSearchOptions}
                  placeholder="Enter user name/email/ID (min 3 characters)"
                  onSelect={handleSelectedUserSearchSelect}
                  onSearch={handleSelectedUserSearch}
                  searchButton={false}
                  value={selectedUserSearchInput}
                  isOpenOverride={isSelectedUserSearchOpen}
                  setIsOpenOverride={setIsSelectedUserSearchOpen}
                  isLoading={isSelectedUserSearchLoading}
                  getDisplayText={(option: string) => option || ''}
                />
              </ControlsRow>

              <SectionSubtitle>
                Current selection: <strong>{selectedUserName}</strong> ({selectedUserId})
              </SectionSubtitle>

              <ControlsRow>
                <Select
                  value={selectedUserId}
                  onChange={(event) => {
                    const nextId = event.target.value;
                    setSelectedUserId(nextId);
                    if (nextId === currentUserId) {
                      setSelectedUserName(
                        getUserDisplayNameFromRecord(
                          currentUserRecord,
                          nextId,
                        ),
                      );
                    } else {
                      setSelectedUserName(nextId);
                    }
                  }}
                >
                  {availableUserOptions.map((option) => (
                    <option key={option.id} value={option.id}>
                      {option.id === currentUserId ? `${option.label} (you)` : option.label}
                    </option>
                  ))}
                </Select>
              </ControlsRow>
            </>
          )}

          {auditMode === 'allData' && (
            <SectionSubtitle>
              Showing Litium-wide audit events in the selected time range.
            </SectionSubtitle>
          )}

          {isAuditLoading ? (
            <LoadingSpinner size="small" />
          ) : isHydratingUserDetails ? (
            <>
              <SectionSubtitle>
                Preparing user analytics and resolving profile details...
              </SectionSubtitle>
              <LoadingSpinner size="small" />
            </>
          ) : auditError ? (
            <SectionSubtitle>{auditError}</SectionSubtitle>
          ) : (
            <>
              <StatsGrid>
                <StatCard>
                  <StatLabel>Total Audit Events</StatLabel>
                  <StatValue>{auditLogs.length}</StatValue>
                </StatCard>
              </StatsGrid>

              <SectionSubtitle>Top users</SectionSubtitle>
              <HorizontalBars>
                {auditByUser.map(([label, count]) => (
                  <BarRow key={label}>
                    <BarLabel>{label}</BarLabel>
                    <BarTrack>
                      <BarFill
                        $width={(count / Math.max(auditByUser[0]?.[1] ?? 1, 1)) * 100}
                      />
                    </BarTrack>
                    <BarValue>{count}</BarValue>
                  </BarRow>
                ))}
              </HorizontalBars>

              <SectionSubtitle>Top actions</SectionSubtitle>
              <HorizontalBars>
                {auditByAction.map(([action, count]) => (
                  <BarRow key={action}>
                    <BarLabel>{action}</BarLabel>
                    <BarTrack>
                      <BarFill
                        $width={
                          (count / Math.max(auditByAction[0]?.[1] ?? 1, 1)) * 100
                        }
                      />
                    </BarTrack>
                    <BarValue>{count}</BarValue>
                  </BarRow>
                ))}
              </HorizontalBars>

              <SectionSubtitle>Top entity types</SectionSubtitle>
              <HorizontalBars>
                {auditByEntityType.map(([entityType, count]) => (
                  <BarRow key={entityType}>
                    <BarLabel>{entityType}</BarLabel>
                    <BarTrack>
                      <BarFill
                        $width={
                          (count / Math.max(auditByEntityType[0]?.[1] ?? 1, 1)) * 100
                        }
                      />
                    </BarTrack>
                    <BarValue>{count}</BarValue>
                  </BarRow>
                ))}
              </HorizontalBars>

              {auditMode === 'allData' && (
                <>
                  <ControlsRow>
                    <Select
                      value={userGroupFilter}
                      onChange={(event) =>
                        setUserGroupFilter(event.target.value as UserGroupFilter)
                      }
                    >
                      <option value="all">All Users</option>
                      <option value="superUsers">SuperUsers</option>
                    </Select>
                    <Select
                      value={officeFilter}
                      onChange={(event) => setOfficeFilter(event.target.value)}
                    >
                      <option value="all">All Offices</option>
                      {perUserFilterOptions.offices.map((office) => (
                        <option key={office} value={office}>
                          {office}
                        </option>
                      ))}
                    </Select>
                    <Select
                      value={departmentFilter}
                      onChange={(event) => setDepartmentFilter(event.target.value)}
                    >
                      <option value="all">All Departments</option>
                      {perUserFilterOptions.departments.map((department) => (
                        <option key={department} value={department}>
                          {department}
                        </option>
                      ))}
                    </Select>
                    <Select
                      value={countryFilter}
                      onChange={(event) => setCountryFilter(event.target.value)}
                    >
                      <option value="all">All Countries</option>
                      {perUserFilterOptions.countries.map((country) => (
                        <option key={country} value={country}>
                          {country}
                        </option>
                      ))}
                    </Select>
                  </ControlsRow>
                  <SectionSubtitle>Per User</SectionSubtitle>
                  {filteredPerUserStats.length === 0 && (
                    <EmptyState>
                      No users match the current Office/Department/Country filters.
                    </EmptyState>
                  )}
                  <UserCardsGrid>
                    {filteredPerUserStats.map((user) => (
                      <UserCard key={user.userId}>
                        <UserCardTitle>{user.displayName}</UserCardTitle>
                        <UserCardSubtle>
                          {user.userDetails?.jobTitle || 'Job title not available'}
                        </UserCardSubtle>
                        <UserCardSubtle>
                          {user.userDetails?.office || 'Office n/a'} •{' '}
                          {user.userDetails?.department || 'Department n/a'} •{' '}
                          {user.userDetails?.country || 'Country n/a'}
                        </UserCardSubtle>
                        <UserCardSubtle>{user.userId}</UserCardSubtle>
                        <UserCardSubtle>Total events: {user.total}</UserCardSubtle>
                        <UserActionList>
                          {user.topActions.map(([action, count]) => (
                            <UserActionRow key={`${user.userId}-${action}`}>
                              <span>{action}</span>
                              <strong>{count}</strong>
                            </UserActionRow>
                          ))}
                        </UserActionList>
                      </UserCard>
                    ))}
                  </UserCardsGrid>
                </>
              )}
            </>
          )}
        </DashboardSection>
      </ContentContainer>
    </PageContainer>
  );
};
